/* Copyright (c) 2020-present CloudIO Inc */

import { isNotEmpty } from 'utils/isEmpty';
import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn, AliasType } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './SidebarDraggableList';

const SidebarDraggableListManager: ComponentManagerFn<
  'SidebarDraggableList'
> = (platform: Platform) => {
  return {
    ...baseComponentManager<'SidebarDraggableList'>(
      platform,
      'SidebarDraggableList',
    ),
    type: 'SidebarDraggableList',
    name: 'Sidebar Draggable List',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick', 'IconButton'] as ItemType[]).includes(childType),
    defaultValue,
    defaultChildren: ['IconButton'],
    props: {
      alias: {
        label: 'Data Store Alias',
        type: 'DataSourceAliasChooser',
        allowedTypes: ['CloudDataStore', 'ReadOnlyDataStore', 'DataStore'],
      },
      name: {
        label: 'Name',
        type: 'ComboAttributeChooser',
        aliasType: 'LOV',
        getAttributeDataTypes: () => ['String'],
      },
    },
    isPropertyVisible(prop, itemData): boolean {
      switch (prop) {
        case 'name':
          return isNotEmpty(itemData.alias);
        default:
          return true;
      }
    },
    getDisplayText: (item) => item.props.name ?? '',
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'LOV' ? { aliasProp: 'alias' } : undefined,
    isDataSourceAliasUsed: (item, alias) => item.props.alias === alias,
    category: 'Sidebar',
    icon: { iconName: 'line-height' },
    description: `**Overview**<br/>
The Sidebar Draggable List is a navigation and organization component designed specifically for sidebar panels. It renders a list of items derived from a Data Store that users can interact with via drag-and-drop operations. Each item in the list typically features a text label, an optional icon, and action buttons.
Use this component to build dynamic, reorderable menus or task lists within the application's sidebar area.

**1. Data Connection**<br/>
This component is data-driven and requires a connection to a valid Data Store to populate its list items.
* **Data Store Alias:** You must select a Data Store (CloudDataStore, ReadOnlyDataStore, or DataStore) containing the records you wish to display.
* **Name:** The specific attribute (Field) from the selected Data Store that provides the display text for each list item. This property is only configurable after a Data Store Alias has been selected.

**2. Structure & Actions**<br/>
The component is designed to hold specific child elements that define the interactivity of each list item.
* **Default Child:** It comes pre-configured with an IconButton, typically used to trigger a context menu or specific action for that item.
* **IconButton:** For adding secondary actions (e.g., "Edit", "Delete", "More Options") to the right side of the list item.
* **TriggerOnClick:** Allows the entire list row to be clickable, enabling navigation or selection events when a user clicks an item.

**3. Functionality**<br/>
* **Draggable Interface:** As implied by the name, this component enables drag-and-drop interactions. This is particularly useful for interfaces where users need to reorder items or drag an item from the sidebar onto a main canvas or workspace.

**4. Best Use Cases**<br/>
* **Custom Navigation:** Creating a sidebar menu where users can rearrange their favorite links.
* **Tool Palettes:** A list of available widgets or tools that can be dragged onto a design canvas.
* **Task Management:** A "To-Do" list within a sidebar where tasks can be re-prioritized by dragging them up or down.`,
  };
};

export default SidebarDraggableListManager;
